import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { STANDALONE_ICON } from '@common/constants';
import { BrowserStorage } from '@common/enums';
import { ProductMode, SystemInfo } from '@common/models';
import { PosthogService } from '@common/posthog/posthog.service';
import { AbilityService } from '@common/services';
import { AuthEffectService } from '@common/services/auth/auth-effect.service';
import { AuthStateService } from '@common/services/auth/auth-state.service';
import { DomainBootstrapService } from '@common/services/domain/domain-bootstrap.service';
import { SentryService } from '@common/services/sentry.service';
import { PersistentStateService } from '@common/services/storage/persistent-state.service';
import { AuthState } from '@common/types';
import { WINDOW } from '@common/window.provider';
import { ModalService } from 'mbs-ui-kit';
import { forkJoin, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthBootstrapService {
  private readonly ability = inject(AbilityService);
  private readonly authEffect = inject(AuthEffectService);
  private readonly authSate = inject(AuthStateService);
  private readonly document = inject(DOCUMENT);
  private readonly domainBootstrap = inject(DomainBootstrapService);
  private readonly modalService = inject(ModalService);
  private readonly persistentService = inject(PersistentStateService);
  private readonly phService = inject(PosthogService);
  private readonly sentryService = inject(SentryService);
  private readonly window = inject(WINDOW);

  private persistentStateKey: string;

  resolveAuthGuard(): Observable<boolean> {
    return forkJoin({
      availableServices: this.authEffect.getAvailableServices(),
      authUser: this.authEffect.getAuthUser(),
      claims: this.authEffect.getClaims(),
      defaultObjectLockCount: this.authEffect.checkDefaultObjectLock(),
      systemInfo: this.authEffect.getSystemInfo()
    }).pipe(
      tap(this.setPersistentStateKey.bind(this)),
      tap(this.initialize.bind(this)),
      tap(() => this.modalService.dismissAll()),
      switchMap(() => (this.ability.isAdmin() ? this.domainBootstrap.resolveAuthGuard() : of(true)))
    );
  }

  private setPersistentStateKey(data: Partial<AuthState>): void {
    const { authUser } = data || {};

    if (!this.persistentStateKey && authUser?.Id) {
      const payload = { Id: authUser.Id, HostName: this.window.location.hostname };
      const json = JSON.stringify(payload);

      this.persistentStateKey = BrowserStorage.Prefix + this.window.btoa(json);
      this.persistentService.eventEmitter$.emit(this.persistentStateKey);
    }
  }

  private initialize(data: Partial<AuthState>): void {
    const { authUser, systemInfo } = data || {};

    this.authSate.patch(data);
    this.setFavicon(systemInfo);
    this.setTimeZoneOffsetInCookie(); // TODO: temporary method for entrance into admin. Have to delete before release 4.0
    this.phService.initialize(authUser);
    this.sentryService.init(systemInfo);
    this.sentryService.addUser(authUser);
  }

  private setFavicon(systemInfo: SystemInfo): void {
    const faviconUrl = systemInfo.ProductMode === ProductMode.Resale ? STANDALONE_ICON.favicon : systemInfo.FaviconUrl;

    this.document.head.querySelector('#favicon').setAttribute('href', faviconUrl);
  }

  private setTimeZoneOffsetInCookie() {
    const cookieDomain = 'apps-stage.mspbackups.com';
    const options = {
      domain: cookieDomain,
      path: '/',
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
    };

    let cookie = 'offset=' + new Date().getTimezoneOffset();

    for (const key of Object.keys(options)) {
      cookie += '; ' + key + '=' + options[key];
    }

    this.document.cookie = cookie;
  }
}
